/* ==========================================================================
   Collection page — filters, sorting and pagination without page reloads
   Adapter of the shared filter bar (assets/filter-bar.js, loaded right
   before this file): the bar, the mobile sheet and swapping the results live
   there. Here, what is the collection's own: filters are product tags in the
   URL path, /collections/<handle>/<tag>+<tag>, one option per group (Shopify
   combines tags with AND), the groups come from #CollectionFiltersConfig
   (snippets/filter-groups.liquid), and the options that would give no
   results are found with light requests to
   sections/collection-filter-tags.liquid.
   Plus Precio: Shopify's own price filter in the query
   (?filter.v.price.gte=…&filter.v.price.lte=…, FilterBar.price), combined
   with the tags of the path. Linking to another collection ("Tipo de
   producto") drops it, like the tags. collection.tags doesn't take the
   price into account, so with a price the options with no results come
   from counts instead (FilterBar.counts, sections/collection-filter-count.liquid).
   Loaded only from sections/collection-template.liquid.
   ========================================================================== */
(function () {
  'use strict';

  // Already running (e.g. the theme editor inserted the section's script again):
  // the first copy re-initializes itself on shopify:section:load
  if (window.CollectionFilters || !window.FilterBar) return;

  var TAGS_SECTION_ID = 'collection-filter-tags'; // tags only, for the dimmed options
  var COUNT_SECTION_ID = 'collection-filter-count'; // the number of products, with a price
  var readJSON = window.FilterBar.readJSON;
  var price = window.FilterBar.price;

  // The groups of filter-groups (tags or links); Precio is an extra one
  function isTagGroup(group) {
    return !group.extra;
  }

  // Tags are read from what follows /collections/<handle>/ in the path, not by
  // counting segments, so a locale prefix (/en/...) or a trailing slash works.
  // Decoded so "ni%C3%B1o" matches the "niño" option.
  function tagsFromPath(pathname) {
    var match = pathname.match(/\/collections\/[^\/]+\/([^\/]+)/);
    if (!match) return [];
    return match[1].split('+').map(function (tag) {
      try {
        tag = decodeURIComponent(tag);
      } catch (e) {}
      return tag.toLowerCase();
    });
  }

  function stateFromPath(ctx, pathname) {
    var tags = tagsFromPath(pathname);
    var next = {};
    ctx.groups.filter(isTagGroup).forEach(function (filter) {
      filter.options.forEach(function (option) {
        if (option.val && tags.indexOf(option.val.toLowerCase()) > -1) {
          next[filter.name] = option.val;
        }
      });
    });
    return next;
  }

  // Collection path with the tags of a selection: /collections/<handle>/<tag>+<tag>
  function collectionPath(ctx, selection) {
    var tags = [];
    ctx.groups.filter(isTagGroup).forEach(function (filter) {
      if (selection[filter.name]) tags.push(encodeURIComponent(selection[filter.name]));
    });
    return ctx.data.url + (tags.length ? '/' + tags.join('+') : '');
  }

  // The price of a selection as a query string ('' without one)
  function priceQuery(selection) {
    var params = new URLSearchParams();
    price.write(params, selection.price);
    return params.toString();
  }

  // Path and price of a selection: the products it shows
  function viewUrl(ctx, selection) {
    var query = priceQuery(selection);
    return collectionPath(ctx, selection) + (query ? '?' + query : '');
  }

  // A light section about a selection's products (path and price)
  function sectionUrl(ctx, selection, sectionId) {
    var query = priceQuery(selection);
    return collectionPath(ctx, selection) + '?' + (query ? query + '&' : '') + 'section_id=' + sectionId;
  }

  // With a price: one count per option (collection.products_count does
  // take the price into account, collection.tags doesn't)
  var counter = window.FilterBar.counts({
    url: function (ctx, selection) {
      return sectionUrl(ctx, selection, COUNT_SECTION_ID);
    },
    dataId: 'CollectionFilterCount'
  });

  // Collection URL with the checked tags and the price. Keeps the rest of
  // the query string (sort_by…) but drops the page: a new filter or sort
  // order starts on page 1.
  function filteredUrl(ctx, selection, sortBy) {
    var params = new URLSearchParams(location.search);
    params.delete('page');
    price.write(params, selection.price);
    if (sortBy) params.set('sort_by', sortBy);
    var query = params.toString();
    return collectionPath(ctx, selection) + (query ? '?' + query : '');
  }

  // Without a price. Picking another option in a group that already has one
  // replaces its tag, so the current results can't tell whether it gives
  // products. For each such group, ask for the tags of the results with only
  // the other groups' filters (light request,
  // sections/collection-filter-tags.liquid): an option gives products if its
  // tag is there. One request per group, all in parallel.
  // A failed one resolves to nothing, which leaves that group's options enabled.
  function loadGroupTags(ctx, selection, signal) {
    var groups = ctx.groups.filter(function (filter) {
      return isTagGroup(filter) && selection[filter.name];
    });
    return Promise.all(groups.map(function (filter) {
      var others = Object.assign({}, selection);
      delete others[filter.name];
      return fetch(sectionUrl(ctx, others, TAGS_SECTION_ID), { signal: signal, credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var tags = readJSON(doc, 'CollectionFilterTags');
          if (!tags) throw new Error('No tags in the response');
          return tags;
        })
        .catch(function () {
          return null;
        });
    })).then(function (lists) {
      var result = {};
      groups.forEach(function (filter, i) {
        if (lists[i]) result[filter.name] = lists[i];
      });
      return result;
    });
  }

  // Handle of a collection URL: /collections/<handle>…
  function collectionHandle(url) {
    var match = url.match(/\/collections\/([^\/?#]+)/);
    return match ? match[1] : '';
  }

  var bar = window.FilterBar({
    root: '[data-section="CollectionTemplate"]',
    sectionId: 'collection-template',
    prefix: 'CollectionFilter',
    sortId: 'SortBy',
    dataId: 'CollectionFiltersData', // url, count, sort and the tags of the results
    metaId: 'CollectionPageMeta',
    historyKey: 'collectionFilters',
    // Without a price, the results wait for the tags (one request per
    // checked group), so the options are never wrong. With a price, the
    // counts are more requests: the results show first and the options are
    // dimmed when they arrive, as in the search.
    blocking: function (ctx, selection) {
      return !selection.price;
    },

    groups: function (ctx) {
      var groups = readJSON(ctx.root, 'CollectionFiltersConfig');
      if (!groups) return null;
      groups.push(price.group());
      return groups;
    },

    readState: function (ctx) {
      var next = stateFromPath(ctx, location.pathname);
      var range = price.read(new URLSearchParams(location.search));
      if (range) next.price = range;
      return next;
    },

    url: function (ctx, selection, sortBy) {
      return filteredUrl(ctx, selection, sortBy);
    },

    // "Tipo de producto": the current collection is marked
    isCurrentLink: function (ctx, option) {
      return collectionHandle(option.href) === collectionHandle(ctx.data.url);
    },

    // Without a price, groups without a checked option: the tag must be in
    // the current results. Groups with one: in the results with only the
    // other groups' filters (loadGroupTags). With a price: the counts.
    availabilityKey: function (ctx, selection) {
      return viewUrl(ctx, selection);
    },
    loadAvailability: function (ctx, selection, signal) {
      return selection.price ? counter.load(ctx, selection, signal) : loadGroupTags(ctx, selection, signal);
    },
    loadOnInit: function (ctx, selection) {
      return Object.keys(selection).length > 0;
    },
    isEmpty: function (ctx, group, value) {
      if (ctx.state.price) return counter.isEmpty(ctx, group, value);
      var tags = ctx.state[group] ? ctx.avail[group] : ctx.data.tags;
      return !!tags && tags.indexOf(value.toLowerCase()) === -1;
    },

    buildGroup: price.build,
    pillValue: price.pillValue,
    syncGroup: price.sync
  });

  window.CollectionFilters = { sortBy: bar.sortBy };
})();
